/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import EmptyMessage from 'components/@common/EmptyResultSetMessage';
import { getAssignedProducts } from 'utilities/product-utils';
import { Classroom } from 'types/schema-generated-types';
import ClassroomGssSettings from './components/GssSettings';
import ClassroomKeyboardingSettings from './components/KeyboardingSettings';
import ClassroomWdtSettings from './components/WdtSettings';
import ClassroomHwtSettings from './components/HwtSettings';
import useParseActionRouteParams from 'hooks/route-action/use-parse-action-route-params';
import ClassroomAzsappSettings from './components/AzsappSettings';
import ClassroomKeyboardAddonGroupSettings from './components/KeyboardingSettings/ClassroomKeyboardAddonGroup';
import { Divider } from '@material-ui/core';

/*
 TODO: I noticed this SettingsContainer is extremely
 similar to the one for student settings. I think they could
 be refactored to be the same component.

 During the initial refactor I wasn't sure enough about the potential
 differences to commit to a refactor.
*/
interface SettingsContainerProps {
  classroom: Classroom;
}
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  name: string;
  value: string | null;
  dividedGroup?: any;
}

interface A11yProps {
  id: string;
  'aria-controls': string;
}

const PRODUCT_SETTINGS = [
  { productFamilyCode: 'GSS', component: ClassroomGssSettings },
  {
    productFamilyCode: 'KEY',
    component: ClassroomKeyboardingSettings,
    groupedSettings: ClassroomKeyboardAddonGroupSettings
  },
  { productFamilyCode: 'WDT', component: ClassroomWdtSettings },
  { productFamilyCode: 'HWT', component: ClassroomHwtSettings },
  { productFamilyCode: 'AZMM', component: ClassroomAzsappSettings }
];

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    height: '100%'
  },
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
    width: 360
  },
  tabPanel: {
    margin: theme.spacing(2),
    width: `calc(100% - ${theme.spacing(42.75)}px)`
  },
  dividedPanels: {
    width: '100%'
  },
  tabPanelBox: {
    maxWidth: `750px`,
    margin: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column'
  },
  tabButton: {
    alignSelf: 'flex-end',
    width: 222,
    '& span': {
      textAlign: 'left',
      alignItems: 'flex-start'
    }
  }
}));

const TabPanel: React.FC<TabPanelProps> = props => {
  const { children, value, name, index, dividedGroup, ...other } = props;
  const classes = useStyles();

  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== name}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      className={dividedGroup ? classes.dividedPanels : classes.tabPanel}
      {...other}
    >
      {value === name && (
        <>
          <Box className={classes.tabPanelBox}>{children}</Box>
          {dividedGroup && <Divider />}
          <Box className={classes.tabPanelBox}>{dividedGroup && <>{dividedGroup}</>}</Box>
        </>
      )}
    </Typography>
  );
};

const a11yProps = (index: number): A11yProps => ({
  id: `vertical-tab-${index}`,
  'aria-controls': `vertical-tabpanel-${index}`
});

type SettingsContainerActionRouteParams = {
  classroomId: number;
  tab?: string;
  setting?: string;
};

const SettingsContainer: React.FC<SettingsContainerProps> = ({ classroom }) => {
  const classes = useStyles();
  const { t } = useTranslation('Classroom');
  const { t: tProductFamily } = useTranslation('ProductFamily');
  const params = useParseActionRouteParams<SettingsContainerActionRouteParams>();
  const products = classroom.licenseUsage ? getAssignedProducts(classroom.licenseUsage) : [];
  const productsWithSettings = useMemo(
    () =>
      PRODUCT_SETTINGS.reduce((acc, x) => {
        const product = products.find(({ product }) => product.family.code === x.productFamilyCode);
        return product ? [...acc, x] : acc;
      }, [] as { productFamilyCode: string; component: any; groupedSettings?: any }[]),
    [products]
  );
  const [selectedTab, setSelectedTab] = useState<string>(
    params?.tab?.toUpperCase() ?? productsWithSettings[0]?.productFamilyCode
  );
  const [expanded, setExpanded] = useState<string | null>(params?.setting ?? null);

  const handleChangeExpanded = React.useCallback(
    (panel: string | null) => (): void => {
      setExpanded(expanded === panel ? null : panel);
    },
    [expanded, setExpanded]
  );

  const handleChange = (event: React.ChangeEvent<{}>, productFamilyCode: string): void => {
    setSelectedTab(productFamilyCode);
    handleChangeExpanded(null)();
  };

  const tabProps = React.useMemo(() => {
    return {
      classroom,
      expanded,
      handleChangeExpanded
    };
  }, [classroom, expanded, handleChangeExpanded]);

  if (productsWithSettings.length === 0) {
    return (
      <EmptyMessage
        style={{ marginTop: '12px' }}
        message={t('No settings available.')}
        subtitle={t(
          `Students in this class either have no license assigned or are licensed for products with no class settings.`
        )}
      />
    );
  }

  return (
    <div className={classes.root}>
      <Tabs
        orientation="vertical"
        variant="standard"
        textColor="secondary"
        value={selectedTab}
        onChange={handleChange}
        aria-label={t('Student apps')}
        className={classes.tabs}
      >
        {productsWithSettings.map(({ productFamilyCode }, index) => (
          <Tab
            key={`tab-${productFamilyCode}`}
            label={tProductFamily(productFamilyCode)}
            className={classes.tabButton}
            value={productFamilyCode}
            {...a11yProps(index)}
          />
        ))}
      </Tabs>
      {productsWithSettings.map(
        (
          { productFamilyCode, component: SettingsPanel, groupedSettings: GroupedSettings },
          index
        ) => (
          <TabPanel
            key={`${productFamilyCode}-settings`}
            value={selectedTab}
            name={productFamilyCode}
            index={index}
            dividedGroup={GroupedSettings ? <GroupedSettings {...tabProps} /> : null}
          >
            <SettingsPanel {...tabProps} />
          </TabPanel>
        )
      )}
    </div>
  );
};

export default SettingsContainer;
